// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// How far through its travel a mechanism is, measured in simulation steps (M3-05).
//
// A coil fires between one step and the next. The mechanism it drives takes tens of
// milliseconds, and those milliseconds are the difference between a machine that feels alive
// and a set of textures that change colour. This is the arithmetic for them, and it is
// arithmetic rather than an animation system for one reason: **the input is a step count.**
//
// The simulation runs at a fixed 1000 Hz, so a step *is* a millisecond, and a mechanism whose
// progress is a function of `stepIndex − firedAtStep` looks the same on a phone drawing 30
// frames a second as on a desktop drawing 144. Reach for `performance.now()` instead — the
// obvious thing, and what an animation library would do — and the machine's mechanisms drift
// away from its physics whenever the renderer falls behind, which is precisely when a player
// is looking hardest.
//
// Both functions are total. A negative elapsed count means "has not happened", which is the
// state a device is in before its first hit and after every reset, and both answer it with
// the resting value rather than a guess.

/**
 * Steps since a stamp, or −1 if the stamp says "never".
 *
 * **This is not `stepIndex − stamp`, and the difference is a bug I shipped for an hour.**
 * `firedAtStep` and `movedAtStep` are −1 until the mechanism has done anything, so the naive
 * subtraction reads 1 on the very first step of a game and 19 on the nineteenth — squarely
 * inside a bumper's eighteen-step outward stroke. Every bumper on the machine pops once at
 * the start of every ball, and every drop target is drawn sunk into the wood for the first
 * seventy milliseconds, for no reason a player could ever guess at.
 *
 * The sentinel has to survive the subtraction, so it does it here, once, rather than at each
 * of the four call sites where three of them would remember.
 */
export function since(stepIndex: number, stamp: number): number {
  return stamp < 0 ? -1 : stepIndex - stamp
}

/**
 * A there-and-back throw: 0 at rest, 1 at full travel, 0 again once it has returned.
 *
 * `out` and `back` are step counts, and they differ because a real mechanism is not
 * symmetrical: a coil slams its armature out in a dozen milliseconds and a return spring
 * takes four or five times as long to bring it home. Making them equal is the single change
 * that makes a bumper look like a bouncing ball rather than a solenoid.
 *
 * The outward stroke eases *out* (fast at the start, since the coil's force is greatest when
 * the armature is furthest from its seat) and the return eases *in and out*, because a spring
 * accelerates from rest and is stopped by the stop.
 */
export function pulse(elapsedSteps: number, out: number, back: number): number {
  if (elapsedSteps < 0 || elapsedSteps >= out + back) return 0
  if (elapsedSteps < out) {
    const t = elapsedSteps / out
    return 1 - (1 - t) * (1 - t)
  }
  const t = (elapsedSteps - out) / back
  return 1 - smoothstep(t)
}

/**
 * A one-way move: 0 the moment it starts, 1 once it has arrived, and 1 forever after.
 *
 * The caller supplies the *direction* — a drop target uses `isUp` to decide whether it is
 * settling into the wood or rising out of it — so this only answers how far along the travel
 * is. That split is what lets one stamp serve both directions of a mechanism that moves both
 * ways, without a second field recording which way it went.
 */
export function settle(elapsedSteps: number, travel: number): number {
  if (elapsedSteps < 0) return 1
  if (travel <= 0 || elapsedSteps >= travel) return 1
  return smoothstep(elapsedSteps / travel)
}

/** The classic 3t² − 2t³, on an input already known to be within 0…1. */
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t)
}
